import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX, 
  X, 
  Radio,
  Loader2,
  Music2,
  ChevronUp,
  ChevronDown,
  ListMusic,
  ExternalLink,
  User,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * 🎵 GLOBAL RADIO PLAYER - VERSÃO APRIMORADA
 * 
 * Barra fixa no rodapé que aparece quando uma música está tocando.
 * Persiste durante toda a navegação do site como uma rádio de verdade.
 * 
 * MELHORIAS:
 * - Indicador de posição na playlist (ex: 3/10)
 * - Player minimizado mais visível quando tocando
 * - Mantém estado mesmo ao navegar entre páginas
 * - 👍👎 Botões de curtir/descurtir músicas
 */

const GlobalRadioPlayer = () => {
  const navigate = useNavigate();
  const {
    currentTrack,
    isPlaying,
    isVisible,
    volume,
    progress,
    duration,
    isLoading,
    error,
    togglePlayPause,
    stop,
    playNext,
    playPrevious,
    seekTo,
    setVolume,
    playlist,
    currentIndex,
    maximize
  } = useRadioPlayer();
  
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // 🎵 Estado de votação
  const [userVote, setUserVote] = useState(null); // 'like', 'dislike', ou null
  const [likesCount, setLikesCount] = useState(0);
  const [dislikesCount, setDislikesCount] = useState(0);
  const [isVoting, setIsVoting] = useState(false);
  
  // 🎵 Carregar estado de votação quando a música muda
  useEffect(() => {
    const loadVoteStatus = async () => {
      if (!currentTrack?.id) {
        setUserVote(null);
        setLikesCount(0);
        setDislikesCount(0);
        return;
      }
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/gallery/stats/${currentTrack.id}`);
        if (response.data) {
          setLikesCount(response.data.likes_count || 0);
          setDislikesCount(response.data.dislikes_count || 0);
          setUserVote(response.data.user_vote || null);
        }
      } catch (error) {
        console.log('Erro ao carregar votos:', error);
      }
    };
    
    loadVoteStatus();
  }, [currentTrack?.id]);
  
  // 🎵 Função de votar
  const handleVote = async (voteType) => {
    if (!currentTrack?.id || isVoting) return;
    
    setIsVoting(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/gallery/vote/${currentTrack.id}`,
        { vote: voteType }
      );
      
      if (response.data.success) {
        setUserVote(response.data.vote);
        setLikesCount(response.data.likes_count);
        setDislikesCount(response.data.dislikes_count);
        
        if (response.data.vote === 'like') {
          toast.success('👍 Você curtiu esta música!', { duration: 2000 });
        } else if (response.data.vote === 'dislike') {
          toast.info('👎 Você não curtiu esta música', { duration: 2000 });
        } else {
          toast.info('Voto removido', { duration: 1500 });
        }
      }
    } catch (error) {
      console.error('Erro ao votar:', error);
      toast.error('Erro ao registrar voto');
    } finally {
      setIsVoting(false);
    }
  };
  
  // 🔧 VERSÃO ATUAL: Log de montagem/desmontagem
  useEffect(() => {
    console.log('🎵 [GlobalRadioPlayer] MONTADO na página:', window.location.pathname);
    return () => {
      console.log('🎵 [GlobalRadioPlayer] DESMONTADO da página:', window.location.pathname);
    };
  }, []);
  
  // Auto-maximizar quando uma nova música começa a tocar
  useEffect(() => {
    if (currentTrack && isPlaying) {
      setIsMinimized(false);
    }
  }, [currentTrack?.id]);
  
  // 🔧 VERSÃO ATUAL: Log para debug da persistência
  useEffect(() => {
    console.log('🎵 [GlobalRadioPlayer] Estado:', {
      hasTrack: !!currentTrack,
      trackTitle: currentTrack?.title,
      isPlaying,
      isVisible,
      pathname: window.location.pathname
    });
  }, [currentTrack, isPlaying, isVisible]);
  
  // 🔧 VERSÃO ATUAL: CORREÇÃO DEFINITIVA
  // O player só NÃO renderiza se:
  // 1. Não houver música carregada (currentTrack é null)
  // 2. O usuário fechou explicitamente (stop foi chamado, que seta currentTrack para null)
  // Em TODOS os outros casos, o player deve aparecer!
  if (!currentTrack) {
    console.log('🎵 [GlobalRadioPlayer] Não renderizando - sem track');
    return null;
  }
  
  // REMOVIDO: A verificação de isVisible foi removida porque causava problemas
  // O player agora SEMPRE aparece quando há um currentTrack
  
  // Formatar tempo (mm:ss)
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calcular porcentagem de progresso
  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;
  
  // Lidar com clique na barra de progresso
  const handleProgressClick = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    seekTo(newTime);
  };
  
  // Player minimizado (ícone flutuante com animação)
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className={`fixed bottom-4 right-4 z-50 w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-all ${isPlaying ? 'animate-pulse ring-4 ring-purple-400/50' : ''}`}
        title="Abrir Rádio do Compositor"
      >
        <div className="relative">
          {isPlaying ? (
            <>
              <Music2 className="w-7 h-7 animate-bounce" />
              {/* Indicador de posição na playlist */}
              {playlist.length > 1 && (
                <span className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {currentIndex + 1}
                </span>
              )}
            </>
          ) : (
            <Radio className="w-7 h-7" />
          )}
        </div>
      </button>
    );
  }
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 border-t border-purple-500/30 shadow-2xl">
      {/* Barra de Progresso Clicável */}
      <div 
        className="h-1.5 bg-gray-700 cursor-pointer group"
        onClick={handleProgressClick}
      >
        <div 
          className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 transition-all duration-100 group-hover:h-2 relative"
          style={{ width: `${progressPercent}%` }}
        >
          {/* Indicador de posição */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      
      {/* Conteúdo Principal */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          
          {/* Informações da Música */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Capa com animação - CLICÁVEL VERSÃO ATUAL */}
            <div 
              onClick={() => currentTrack?.id && navigate(`/musica/${currentTrack.id}`)}
              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gradient-to-br from-purple-600 to-blue-600 flex-shrink-0 relative cursor-pointer hover:scale-105 transition-transform ${isPlaying ? 'ring-2 ring-purple-400 ring-offset-2 ring-offset-gray-900' : ''}`}
              title="Clique para ver detalhes da música"
            >
              {currentTrack.coverUrl ? (
                <img 
                  src={currentTrack.coverUrl} 
                  alt={currentTrack.title}
                  className={`w-full h-full object-cover ${isPlaying ? 'animate-pulse' : ''}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Music2 className={`w-7 h-7 text-white/80 ${isPlaying ? 'animate-bounce' : ''}`} />
                </div>
              )}
              {isLoading && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <Loader2 className="w-7 h-7 text-white animate-spin" />
                </div>
              )}
              {/* Overlay de hover */}
              <div className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-all">
                <ExternalLink className="w-5 h-5 text-white" />
              </div>
            </div>
            
            {/* Título e Autor - CLICÁVEIS VERSÃO ATUAL */}
            <div className="min-w-0 flex-1">
              {/* 🎵 VERSÃO ATUAL: Título e Autor são links para página da música */}
              <div 
                onClick={() => currentTrack?.id && navigate(`/musica/${currentTrack.id}`)}
                className="cursor-pointer group/link hover:bg-white/5 rounded-lg p-1 -m-1 transition-all duration-200"
                title="Clique para ver detalhes da música e compositor"
              >
                <div className="flex items-center gap-1">
                  <p className="text-white font-semibold text-sm sm:text-base truncate group-hover/link:text-purple-300 transition-colors">
                    {currentTrack.title}
                  </p>
                  <ExternalLink className="w-3.5 h-3.5 text-purple-400 opacity-0 group-hover/link:opacity-100 transition-opacity flex-shrink-0" />
                </div>
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3 text-gray-500 group-hover/link:text-purple-400 transition-colors" />
                  <p className="text-gray-400 text-xs sm:text-sm truncate group-hover/link:text-purple-300 transition-colors">
                    {currentTrack.author}
                  </p>
                </div>
                {/* Badge "Ver Compositor" aparece no hover */}
                <div className="hidden sm:block">
                  <span className="text-[10px] text-purple-400 opacity-0 group-hover/link:opacity-100 transition-opacity">
                    Clique para ver detalhes →
                  </span>
                </div>
              </div>
              {/* Indicador de playlist */}
              {playlist.length > 1 && (
                <div className="flex items-center gap-1 text-purple-400 text-xs mt-0.5">
                  <ListMusic className="w-3 h-3" />
                  <span>{currentIndex + 1} de {playlist.length} músicas</span>
                </div>
              )}
              {error && (
                <p className="text-red-400 text-xs">{error}</p>
              )}
            </div>
          </div>
          
          {/* Controles Centrais */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Anterior */}
            {playlist.length > 1 && (
              <button
                onClick={playPrevious}
                className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                title="Música Anterior"
              >
                <SkipBack className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            )}
            
            {/* Play/Pause */}
            <button
              onClick={togglePlayPause}
              disabled={isLoading}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 flex items-center justify-center text-white transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 shadow-lg shadow-purple-500/30"
              title={isPlaying ? 'Pausar' : 'Tocar'}
            >
              {isLoading ? (
                <Loader2 className="w-7 h-7 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-7 h-7" />
              ) : (
                <Play className="w-7 h-7 ml-1" />
              )}
            </button>
            
            {/* Próxima */}
            {playlist.length > 1 && (
              <button
                onClick={playNext}
                className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                title="Próxima Música"
              >
                <SkipForward className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            )}
          </div>
          
          {/* 👍👎 BOTÕES DE CURTIR/DESCURTIR */}
          {currentTrack?.id && (
            <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-gray-800/60 rounded-full border border-gray-700/50">
              {/* Botão LIKE */}
              <button
                onClick={() => handleVote('like')}
                disabled={isVoting}
                className={`group flex items-center gap-1 px-2 py-1.5 rounded-full transition-all duration-200 ${
                  userVote === 'like'
                    ? 'bg-green-600 text-white shadow-lg shadow-green-500/40 scale-105'
                    : 'bg-gray-700/50 text-gray-400 hover:bg-green-600/20 hover:text-green-400'
                } ${isVoting ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={userVote === 'like' ? 'Remover curtida' : 'Curtir música'}
              >
                <ThumbsUp className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform ${userVote === 'like' ? 'scale-110' : 'group-hover:scale-110'}`} />
                <span className="text-xs sm:text-sm font-semibold min-w-[20px] text-center">
                  {likesCount}
                </span>
              </button>
              
              {/* Separador visual */}
              <div className="w-px h-6 bg-gray-600/50" />
              
              {/* Botão DISLIKE */}
              <button
                onClick={() => handleVote('dislike')}
                disabled={isVoting}
                className={`group flex items-center gap-1 px-2 py-1.5 rounded-full transition-all duration-200 ${
                  userVote === 'dislike'
                    ? 'bg-red-600 text-white shadow-lg shadow-red-500/40 scale-105'
                    : 'bg-gray-700/50 text-gray-400 hover:bg-red-600/20 hover:text-red-400'
                } ${isVoting ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={userVote === 'dislike' ? 'Remover voto' : 'Não gostei'}
              >
                <ThumbsDown className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform ${userVote === 'dislike' ? 'scale-110' : 'group-hover:scale-110'}`} />
                <span className="text-xs sm:text-sm font-semibold min-w-[20px] text-center">
                  {dislikesCount}
                </span>
              </button>
            </div>
          )}
          
          {/* Tempo e Controles */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Tempo */}
            <div className="text-gray-400 text-xs sm:text-sm hidden sm:flex items-center gap-1 font-mono">
              <span className="text-white">{formatTime(progress)}</span>
              <span className="text-gray-500">/</span>
              <span>{formatTime(duration)}</span>
            </div>
            
            {/* Volume */}
            <div className="relative hidden sm:block">
              <button
                onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                title="Volume"
              >
                {volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              
              {showVolumeSlider && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-gray-800 rounded-lg p-3 shadow-xl border border-purple-500/30">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="w-28 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                  />
                  <div className="text-center text-xs text-gray-400 mt-1">
                    {Math.round(volume * 100)}%
                  </div>
                </div>
              )}
            </div>
            
            {/* Minimizar */}
            <button
              onClick={() => setIsMinimized(true)}
              className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full hidden sm:block"
              title="Minimizar Player"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
            
            {/* Fechar */}
            <button
              onClick={stop}
              className="text-white/70 hover:text-red-400 transition-colors p-2 hover:bg-red-500/10 rounded-full"
              title="Fechar Rádio"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Badge "Rádio do Compositor" com indicador de tocando */}
      <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs px-4 py-1.5 rounded-t-lg flex items-center gap-2 shadow-lg">
        <Radio className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse' : ''}`} />
        <span className="font-medium">Rádio do Compositor</span>
        {isPlaying && (
          <span className="flex gap-0.5">
            <span className="w-1 h-3 bg-white/80 rounded-full animate-pulse" style={{animationDelay: '0ms'}} />
            <span className="w-1 h-4 bg-white/80 rounded-full animate-pulse" style={{animationDelay: '150ms'}} />
            <span className="w-1 h-2 bg-white/80 rounded-full animate-pulse" style={{animationDelay: '300ms'}} />
          </span>
        )}
      </div>
    </div>
  );
};

export default GlobalRadioPlayer;
